import React, { useEffect, useState } from 'react'
import '../css/policy.css';
import { Helmet } from 'react-helmet';
import { fetchPageContent, contentLines, contentText } from '../utils/siteContent';

const DEFAULT_GENERAL = [
    'No food, gum or drink in the studios (with the exception of water).',
    "Attendance is very important to ensure each student's progress. Students who miss 3 consecutive classes without prior arrangement may be dismissed from upcoming performances.",
    'To avoid injury, students should arrive on time for a proper warm-up. If a student is more than 10 min late, they may be asked to observe.',
    'Promotions to higher class levels will be decided by the teacher & owner (by invitation or audition).',
    'If a student causes damage to the studio, the student/parent will be held responsible.',
    'Note: the ballet barres are not to be hung on. They are mounted for balance, not holding weight.',
    'If a student is being disruptive, they may be excused for the remainder of the class. The teacher will fill out a form to document what occurred.',
    'Pictures will be taken. Please let us know if you do not want pictures of your child published.',
];

const DEFAULT_DROP_OFF = [
    'Policy:',
    "After class is over, we would appreciate it if your child is left no longer than 5 minutes to be picked up. If you are running late, please call the studio cell number so that we can make arrangements. Please instruct your child to wait inside of the studio. We do not have office staff available to watch your child. If you are late, you assume full responsibility for your child's welfare.",
    'Assumption of Responsibilities and Risks:',
    'Please be aware that dance, like any physical activity, comes with the risk of potential injury. Registration at In Motion Dance Studio includes understanding and accepting the risk and assuming responsibility for the safety of your child. We will take every step possible to provide a safe and healthy learning environment for you and your dancer.',
];

const DEFAULT_DRESS_CODE_GENERAL = [
    'Dancers must adhere to the dress code by coming prepared to class. Wear appropriate shoes and proper dance clothing. Hair must be out of face. Please make sure your child gets the appropriate dance attire. We plan to incorporate some of these items into open house/recital costumes to keep fees down.',
    'Note: No boy-cut shorts without tights, tank tops, leotards without tights, low cut leotards, backless leotards or midriff baring attire.',
    'Mini Combo & Creative – Bare feet, footless tights, any form fitting dance attire. Dress ups and tutus are fine.',
    'Combos:',
    'BTT+ & BTT Combo – (Ballet, Tap, Tumbling) - Black short sleeve leotard, pink convertible tights, black tap shoes & pink ballet slippers. (skirts & tutus are okay)',
    'BJT Combo – (Ballet, Jazz, Tumbling) Black short sleeve leotard, pink convertible tights & ballet slippers (skirts & tutus are okay)',
    'PreSchool Dance - Please wear comfortable clothing and bring ballet shoes.',
    'Jazz:',
    'Jazz 2/3 and 4/5 – Form fitting dance clothing with sleeves, no tanks tops, bare feet or Footundees. Hair must be pulled back out of the face.',
    'Jazz/Hip Hop combo – Short sleeve leotard (any color), black leggings and bare feet for Jazz, tennis shoes for Hip Hop.',
    'Hip Hop & Break – Street clothes, with non-marking tennis shoes. No tank tops.',
    'Contemporary (Lyrical) – Form fitting dance attire and bare feet.',
    'Ballet – Required ballet class clothing (does NOT include Ballet shoes) — see prices below.',
    'Tap – Tap shoes and street clothing.',
    'Tumblers – Form fitting attire, a short sleeved leotard and bottoms or shirts that are long enough to keep stomach covered.',
];

const DEFAULT_DRESS_CODE_PRICES = [
    'Ballet 1 – $55',
    'Ballet 2/3 – $60',
    'Ballet 4/5 – $65',
    'Pre Pointe & Pointe – $70',
];

export default function Policy() {
    const [content, setContent] = useState(null);

    useEffect(() => {
        fetchPageContent('Policy').then(setContent);
    }, []);

    return (
        <div className="policy-content">
            <Helmet>
                <title>In Motion Dance | Policies &#38; Dress Code</title>
            </Helmet>
            <h1>Policies and Dress Code</h1>

            <div className="policy-general">
                <h2>General</h2>
                {contentLines(content, 'general', DEFAULT_GENERAL).map((line, i) => <p key={i}>{line}</p>)}
            </div>

            <div className="policy-dropOff">
                <h2>Pick-Up / Drop-Off</h2>
                {contentLines(content, 'drop_off', DEFAULT_DROP_OFF).map((line, i) => <p key={i}>{line}</p>)}
            </div>

            <div className="policy-addTransfer">
                <h2>Adding / Transferring Classes</h2>
                <p>{contentText(content, 'add_transfer', 'If a student wishes, they may transfer to a different class as long as there are openings in the class. After notifying office personnel, and if the class is the same price, no other action is needed. To add additional classes, a student must notify office personnel, so that the class can be added to tuition. If adding in the middle of a month, a student will pay a prorated class rate until the new month begins.')}</p>
            </div>

            <div className="policy-latefee">
                <h2>Late Fees</h2>
                <p>{contentText(content, 'late_fee', 'If tuition is not paid in full by the 7th day of each month, a $10 per week late fee will begin to be assessed until tuition is paid in full.')}</p>
            </div>

            <div className="policy-cancelation">
                <h2>Canceling Classes</h2>
                {contentLines(content, 'cancellation', [
                    'To cancel classes, you must notify us in writing by email, phone, or text before the 16th of the month prior to the month you intend to stop attending classes. If notice is received between the 16th and the end of the month, a $20 late cancellation fee will apply. Cancellations made between the 1st and 7th of the month are eligible for a 50% tuition refund for the current month. Cancellations made after the 7th of the month are not eligible for a tuition refund for the current month. Please contact us if you do not receive a cancellation confirmation by email or text.',
                    'If cancelling for one month (exceptions are made for illness and injury), you may lose your spot in the class and will need to re-enroll in the studio. This includes having to pay another registration fee.',
                ]).map((line, i) => <p key={i}>{line}</p>)}
            </div>

            <div className="policy-makeup">
                <h2>Make-Up Classes</h2>
                <p>{contentText(content, 'makeup', 'If a student misses a class or we are closed for a holiday, students are invited to attend any class that is their same level during the week. Classes must be "made up" within 30 days of the missed class. They do not carry over. We also allow for anticipatory make-ups, meaning if students know that they will be out-of-town for a certain week, they can make-up the class before they leave. Make sure to inform the teacher that the student is doing a make-up class.')}</p>
            </div>

            <div className="policy-endOfSeason">
                <h2>End Of Season Information</h2>
                <p>{contentText(content, 'end_of_season', 'Fall/Spring dance season culminates in our Spring Recital on Saturday, June 20th. Classes end Friday, June 19th. Tuition will not be charged in July and August, unless you register for summer session.')}</p>
            </div>

            <div className="policy-dressCode">
                <h2>Dress Code</h2>
                {contentLines(content, 'dress_code_general', DEFAULT_DRESS_CODE_GENERAL).map((line, i) => <p key={i}>{line}</p>)}
                <p className="policy-dressCodePricesLabel"><strong>Ballet Prices:</strong></p>
                {contentLines(content, 'dress_code_ballet_prices', DEFAULT_DRESS_CODE_PRICES).map((line, i) => <p key={i}>{line}</p>)}
            </div>
        </div>
    )
}
